import { useState } from "react";
import AccordionCard from "../shared/AccordionCard";
import Toast from "../shared/Toast";

interface ReviewPayload {
  preparation: number;
  execution: number;
  completion: number;
  overallScore: number;
  comments: string;
}

interface CaregiverReviewPanelProps {
  onAccept?: (
    review: ReviewPayload
  ) => Promise<void>;

  onReject?: (
    review: ReviewPayload
  ) => Promise<void>;
}

interface ScoreSelectorProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

function ScoreSelector({
  label,
  value,
  onChange,
  disabled = false,
}: ScoreSelectorProps) {
  return (
    <div className="rating-group">
      <label className="rating-label">
        {label}
      </label>

      <div className="overall-rating-row">
        {[1, 2, 3, 4, 5].map((score) => (
          <button
            key={score}
            type="button"
            disabled={disabled}
            className={`overall-score-circle ${
              value === score
                ? "overall-score-circle--active"
                : ""
            }`}
            onClick={() => onChange(score)}
          />
        ))}
      </div>

      <div className="overall-rating-numbers">
        {[1, 2, 3, 4, 5].map((score) => (
          <span key={score}>{score}</span>
        ))}
      </div>
    </div>
  );
}

function CaregiverReviewPanel({
  onAccept,
  onReject,
}: CaregiverReviewPanelProps) {
  const [preparation, setPreparation] =
    useState(0);

  const [execution, setExecution] =
    useState(0);

  const [completion, setCompletion] =
    useState(0);

  const [overallScore, setOverallScore] =
    useState(0);

  const [comments, setComments] =
    useState("");

  const [reviewSubmitted, setReviewSubmitted] =
    useState(false);

  const [showToast, setShowToast] =
    useState(false);

  const [commentsError, setCommentsError] =
    useState(false);

  const [toastMessage, setToastMessage] =
    useState("");

  const [submitting, setSubmitting] =
  useState(false);

  const isReviewValid =
    preparation > 0 &&
    execution > 0 &&
    completion > 0 &&
    overallScore > 0;

  function displayToast(message: string) {
    setToastMessage(message);
    setShowToast(true);

    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  }

async function handleAccept() {
  if (!isReviewValid) {
    return;
  }

  const payload: ReviewPayload = {
    preparation,
    execution,
    completion,
    overallScore,
    comments,
  };

  try {
    setSubmitting(true);

    await onAccept?.(payload);

    setReviewSubmitted(true);

    displayToast(
      "The Care Coordinator review is submitted successfully."
    );
  } catch (error) {
    console.error(error);

    displayToast(
      "Failed to submit review."
    );
  } finally {
    setSubmitting(false);
  }
}

 async function handleReject() {
  if (!isReviewValid) {
    return;
  }

  const trimmedComments =
    comments.trim();

  if (!trimmedComments) {
    setCommentsError(true);
    return;
  }

  setCommentsError(false);

  const payload: ReviewPayload = {
    preparation,
    execution,
    completion,
    overallScore,
    comments: trimmedComments,
  };

  try {
    setSubmitting(true);

    await onReject?.(payload);

    setReviewSubmitted(true);

    displayToast(
      "The reason for rejection submitted successfully."
    );
  } catch (error) {
    console.error(error);

    displayToast(
      "Failed to submit review."
    );
  } finally {
    setSubmitting(false);
  }
}

  return (
    <>
      <AccordionCard title="CARE COORDINATOR REVIEW">
        <div className="review-panel">
          <div className="review-top-grid">
            <ScoreSelector
              label="Preparation"
              value={preparation}
              onChange={setPreparation}
              disabled={reviewSubmitted || submitting}
            />

            <ScoreSelector
              label="Execution"
              value={execution}
              onChange={setExecution}
              disabled={reviewSubmitted || submitting}
            />

            <ScoreSelector
              label="Completion"
              value={completion}
              onChange={setCompletion}
              disabled={reviewSubmitted || submitting}
            />
          </div>

          <hr className="review-divider" />

          <div className="review-bottom-grid">
            <ScoreSelector
              label="Overall Score"
              value={overallScore}
              onChange={setOverallScore}
              disabled={reviewSubmitted || submitting}
            />

            <div className="feedback-section">
              <label
                className={`rating-label ${
                  commentsError
                    ? "rating-label--error"
                    : ""
                }`}
              >
                Feedback/Comments
              </label>

              {reviewSubmitted ? (
                <div className="feedback-display">
                  {comments || "-"}
                </div>
              ) : (
                <>
                  <textarea
                    className={`feedback-textarea ${
                      commentsError
                        ? "feedback-textarea--error"
                        : ""
                    }`}
                    maxLength={500}
                    placeholder="Feedback/Comments here..."
                    value={comments}
                    disabled={reviewSubmitted || submitting}
                    onChange={(e) => {
                      setComments(e.target.value);

                      if (
                        e.target.value.trim()
                      ) {
                        setCommentsError(false);
                      }
                    }}
                  />

                  <span className="feedback-helper">
                    *max 500 characters
                  </span>

                  {commentsError && (
                    <span className="feedback-error">
                      Feedback/Comments is required.
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {!reviewSubmitted && (
            <div className="review-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleReject}
                disabled={
                  !isReviewValid || submitting
                }
              >
                {submitting ? "Submitting..." : "Reject"}
              </button>

              <button
                type="button"
                className="btn-accept"
                onClick={handleAccept}
                disabled={
                  !isReviewValid || submitting
                }
              >
                {submitting ? "Submitting...": "Accept"}
              </button>
            </div>
          )}
        </div>
      </AccordionCard>

      <Toast
        show={showToast}
        message={toastMessage}
      />
    </>
  );
}

export default CaregiverReviewPanel;